import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  TextField,
  FormControl,
  Autocomplete,
  Divider,
  Typography,
  InputAdornment,
} from "@mui/material";
import { Row, Col } from "react-bootstrap";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "dayjs/locale/en-gb";
import MapLocation from "../mapLocation/mapLocation";
import { baseUrl } from "../../../../../../../../../../../network/constants/constants";
import axios from "axios";
import { getConfig } from "../../../../../../../../../../../network/auth";

dayjs.extend(customParseFormat);
dayjs.locale("en-gb");

// Helper: check if booking is in accepted/in-progress status (payment editable)
const isPaymentEditable = (booking) => {
  if (!booking) return false;
  const status = (booking.status || booking.booking_status || "").toLowerCase();
  // Adjust these status values to match your actual API status strings
  return (
    status === "accepted" ||
    status === "estimated" ||
    status === "inprogress" ||
    status === "in_progress" ||
    status === "in progress" ||
    status === "ongoing" ||
    status === "completed"
  );
};

// Helper: check if booking fields (service/worker/date etc.) are editable
const isBookingFieldsEditable = (booking) => {
  if (!booking) return true; // new booking — all editable
  const status = (booking.status || booking.booking_status || "").toLowerCase();
  return status === "waiting" || status === "pending" || status === "";
};


const STATUS_OPTIONS = ["Waiting", "Accepted", "Cancelled", "Estimated", "Completed"];

const BookingAddEditForm = ({
  open,
  onClose,
  booking,
  serviceList,
  onSubmit,
  setLatitude,
  setLongitude,
  latitude,
  longitude,
  setLoading,
  selectedService,
  setSelectedService,
  workerList,
  setWorkerList,
}) => {
  const [formState, setFormState] = useState({
    serviceName: "",
    worker: "",
    customerName: "",
    phone: "",
    address: "",
    location: "",
    bookingDate: null,
    bookingTime: null,
    bookingPrice: "",
    serviceCharge: "",
    miscellaneousPrice: "",
    totalPrice: "",
    status: "",
    refund: false,
  });

  const [errors, setErrors] = useState({});

  const paymentEditable = isPaymentEditable(booking);
  const bookingFieldsEditable = isBookingFieldsEditable(booking);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [previousStatus, setPreviousStatus] = useState("");

  const serviceNamesWithIds = serviceList.map((service) => ({
    id: service.id,
    name: service.service,
  }));

  const workerNamesWithIds = workerList.map((worker) => ({
    id: worker.id,
    name: worker.worker_name,
  }));

  useEffect(() => {
    if (booking) {
      if (booking.service_id) {
        setSelectedService(booking.service_id);
      }

      const dateStr = booking.date || booking.booked_date;
      let parsedDate = null;
      if (dateStr) {
        parsedDate = dayjs(dateStr, "DD-MM-YYYY");
        if (!parsedDate.isValid()) {
          parsedDate = null;
        }
      }

      const timeString = booking.booked_time;
      let parsedTime = null;
      if (timeString) {
        const [hours, minutes] = timeString.split(":");
        parsedTime = dayjs()
          .set("hour", parseInt(hours))
          .set("minute", parseInt(minutes))
          .set("second", 0);
      }

      setFormState({
        serviceName: booking.service_id || "",
        worker: booking.worker || "",
        customerName: booking.customer_name || booking.name || "",
        phone: booking.phone || "",
        address: booking.customer_address || booking.address || "",
        bookingDate: parsedDate,
        bookingTime: parsedTime,
        serviceCharge: booking.service_charge || "",
        miscellaneousPrice: booking.miscellaneous_price || "",
        totalPrice: booking.total_price || "",
        bookingPrice: booking.booking_price || "",
        status: booking.status || booking.booking_status || "",
        refund: booking.refund || false, 
      });
      setLatitude(booking.latitude || "");
      setLongitude(booking.longitude || "");
    } else {
      setSelectedService("");
      setFormState({
        serviceName: "",
        worker: "",
        customerName: "",
        phone: "",
        address: "",
        bookingDate: null,
        bookingTime: null,
        bookingPrice: "",
        serviceCharge: "",
        miscellaneousPrice: "",
        totalPrice: "",
        status: "",
        refund: false,
      });
      setLatitude("");
      setLongitude("");
    }
    setErrors({});
  }, [booking, setLatitude, setLongitude, setSelectedService]);

  // Auto-calculate total: booking_price (fixed) + service_charge + miscellaneous_price
  useEffect(() => {
    if (paymentEditable) {
      const booking = parseFloat(formState.bookingPrice) || 0;
      const service = parseFloat(formState.serviceCharge) || 0;
      const misc = parseFloat(formState.miscellaneousPrice) || 0;
      const total = booking + service + misc;
      setFormState((prev) => ({
        ...prev,
        totalPrice: total > 0 ? total.toFixed(2) : "",
      }));
    }
  }, [formState.bookingPrice, formState.serviceCharge, formState.miscellaneousPrice, paymentEditable]);

  const config = useMemo(() => {
    return getConfig();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));

    if (name === "serviceName") {
      setSelectedService(value);
      setFormState((prevData) => ({
        ...prevData,
        worker: "",
      }));
    }
  };


  const handleStatusChange = (event, newValue) => {
    const value = newValue || "";

    if (value === "Cancelled") {
      setPreviousStatus(formState.status);
      setFormState((prev) => ({ ...prev, status: value }));
      setRefundDialogOpen(true); // popup: refund venamo?
    } else {
      setFormState((prev) => ({ ...prev, status: value, refund: false }));
    }
  };

  const handleRefundChoice = (refundValue) => {
    setFormState((prev) => ({ ...prev, refund: refundValue }));
    setRefundDialogOpen(false);
  };

  const handleRefundDialogClose = () => {
    // popup close akkiyal (choice illathe) status pazhayathilekk revert cheyyum
    setFormState((prev) => ({ ...prev, status: previousStatus }));
    setRefundDialogOpen(false);
  };

  const fetchServiceManListByServiceId = () => {
    if (!selectedService) return;

    setLoading(true);

    const url = `${baseUrl}superadmin/workers-by-service/${selectedService}`;

    axios
      .get(url, config)
      .then((response) => {
        setWorkerList(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Service Man Error:", error);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchServiceManListByServiceId();
  }, [selectedService]);

  const handleDateChange = (date) => {
    setFormState((prevData) => ({
      ...prevData,
      bookingDate: date,
    }));
  };

  const handleTimeChange = (time) => {
    setFormState((prevData) => ({
      ...prevData,
      bookingTime: time,
    }));
  };

  const validation = () => {
    const newError = {};

    // Basic fields - validate only if editable
    if (bookingFieldsEditable) {
      if (!formState.serviceName)
        newError.serviceName = "Please select a service";
      if (!formState.worker) newError.worker = "Please select a Technician";
    }

    if (!formState.customerName)
      newError.customerName = "Customer Name is required";
    if (!formState.phone)
      newError.phone = "Phone number is required";
    else if (!/^\d{10}$/.test(formState.phone.replace(/\s+/g, "")))
      newError.phone = "A valid 10-digit phone number is required";
    if (!formState.address) newError.address = "Address is required";

    // Payment validation - only if payment section is visible
    if (paymentEditable) {
      if (
        formState.serviceCharge !== "" &&
        isNaN(parseFloat(formState.serviceCharge))
      ) {
        newError.serviceCharge = "Enter a valid amount";
      }
      if (
        formState.miscellaneousPrice !== "" &&
        isNaN(parseFloat(formState.miscellaneousPrice))
      ) {
        newError.miscellaneousPrice = "Enter a valid amount";
      }
    }

    setErrors(newError);
    return Object.keys(newError).length === 0;
  };

  const handleSubmit = () => {
    if (validation()) {
      const formattedDate = formState?.bookingDate
        ? formState.bookingDate.format("DD-MM-YYYY")
        : null;
      const formattedTime = formState?.bookingTime
        ? dayjs(formState?.bookingTime).format("HH:mm:ss")
        : null;
      const bookingDetails = {
        ...formState,
        bookingDate: formattedDate,
        bookingTime: formattedTime,
        status: formState.status,
        refund: formState.status === "Cancelled" ? formState.refund : false,
      };
      onSubmit(bookingDetails);
      // onClose is handled by parent after API success
    }
  };

  const today = dayjs();
  const maxDay = today.add(7, "day");

  // Status badge color helper
  const getStatusColor = (booking) => {
    const status = (booking?.status || booking?.booking_status || "").toLowerCase();
    if (status === "waiting" || status === "pending") return { bg: "#fff3e0", color: "#e65100", border: "#ffb74d" };
    if (status === "accepted") return { bg: "#e8f5e9", color: "#2e7d32", border: "#81c784" };
    if (status === "completed") return { bg: "#e3f2fd", color: "#1565c0", border: "#64b5f6" };
    return { bg: "#f5f5f5", color: "#616161", border: "#bdbdbd" };
  };

  const statusColors = getStatusColor(booking);

  return (
    <>
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle style={{ color: "#ff4d4d" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span>{booking ? "Update Booking Details" : "Add Booking Details"}</span>
          {booking && (booking.status || booking.booking_status) && (
            <span
              style={{
                fontSize: "12px",
                fontWeight: 600,
                padding: "3px 10px",
                borderRadius: "20px",
                backgroundColor: statusColors.bg,
                color: statusColors.color,
                border: `1px solid ${statusColors.border}`,
                textTransform: "capitalize",
              }}
            >
              {booking.status || booking.booking_status}
            </span>
          )}
        </div>
      </DialogTitle>

      <DialogContent>
        <Row>
          <Col lg={4}>
            {/* ── SERVICE & WORKER ── */}
            <FormControl
              fullWidth
              error={!!errors.serviceName}
              className="mt-2"
            >
              <Autocomplete
                options={serviceNamesWithIds}
                getOptionLabel={(option) => option.name}
                value={
                  serviceNamesWithIds.find(
                    (service) => service.id === formState.serviceName
                  ) || null
                }
                onChange={(event, newValue) => {
                  handleChange({
                    target: {
                      name: "serviceName",
                      value: newValue ? newValue.id : "",
                    },
                  });
                }}
                disabled={!bookingFieldsEditable}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Service"
                    error={!!errors.serviceName}
                    helperText={errors.serviceName}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </FormControl>

            <FormControl fullWidth error={!!errors.worker} className="mt-2">
              <Autocomplete
                options={workerNamesWithIds}
                getOptionLabel={(option) => option.name}
                value={
                  workerNamesWithIds.find(
                    (worker) => worker.id === formState.worker
                  ) || null
                }
                onChange={(event, newValue) => {
                  setFormState((prevData) => ({
                    ...prevData,
                    worker: newValue ? newValue.id : "",
                  }));
                  setErrors((prevErrors) => ({ ...prevErrors, worker: "" }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Technician"
                    error={!!errors.worker}
                    helperText={errors.worker}
                    placeholder="Search technician..."
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText={
                  selectedService
                    ? "No technicians available for this service"
                    : "Please select a service first"
                }
                disabled={!bookingFieldsEditable || !selectedService}
              />
            </FormControl>


            {booking && (
              <FormControl fullWidth className="mt-2">
                <Autocomplete
                  options={STATUS_OPTIONS}
                  value={formState.status || null}
                  onChange={(event, newValue) => handleStatusChange(event, newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Booking Status" />
                  )}
                />
                {formState.status === "Cancelled" && (
                  <Typography
                  variant="caption"
                  sx={{ mt: 0.5, color: "#1565c0", cursor: "pointer" }}
                  onClick={() => setRefundDialogOpen(true)}
                >
                  Refund: {formState.refund ? "Yes" : "No"} (click to change)
                </Typography>
                )}
              </FormControl>
            )}

            {/* ── CUSTOMER INFO ── */}
            <TextField
              label="Name"
              fullWidth
              margin="dense"
              name="customerName"
              value={formState.customerName}
              onChange={handleChange}
              error={!!errors.customerName}
              helperText={errors.customerName}
            />
            <TextField
              label="Phone"
              fullWidth
              margin="dense"
              name="phone"
              value={formState.phone}
              onChange={handleChange}
              error={!!errors.phone}
              helperText={errors.phone}
            />
            <TextField
              label="Address"
              fullWidth
              margin="dense"
              name="address"
              multiline
              rows={3}
              value={formState.address}
              onChange={handleChange}
              error={!!errors.address}
              helperText={errors.address}
            />

            {/* ── DATE & TIME ── */}
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
              <DatePicker
                label="Booking Date"
                renderInput={(params) => (
                  <TextField {...params} fullWidth margin="dense" />
                )}
                slotProps={{
                  textField: { error: false },
                }}
                onChange={handleDateChange}
                sx={{ width: "100%", marginTop: "7px" }}
                value={formState.bookingDate}
                format="DD/MM/YYYY"
                minDate={today}
                maxDate={maxDay}
                disabled={!bookingFieldsEditable}
              />
              <TimePicker
                label="Booking Time"
                slotProps={{
                  textField: { error: false },
                }}
                onChange={handleTimeChange}
                viewRenderers={{
                  hours: renderTimeViewClock,
                  minutes: renderTimeViewClock,
                  seconds: renderTimeViewClock,
                }}
                sx={{ width: "100%", marginTop: "7px" }}
                value={
                  formState.bookingTime && dayjs(formState.bookingTime).isValid()
                    ? formState.bookingTime
                    : null
                }
                disabled={!bookingFieldsEditable}
              />
            </LocalizationProvider>

            {/* ── PAYMENT SECTION (only for accepted/inprogress/completed) ── */}
            {paymentEditable && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "#2e7d32",
                    fontWeight: 700,
                    mb: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  Payment Details
                </Typography>

                <TextField
                  label="Booking Price (Fixed)"
                  fullWidth
                  margin="dense"
                  name="bookingPrice"
                  value={formState.bookingPrice}
                  disabled
                  type="number"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                    readOnly: true,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f5f5f5",
                    },
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#555",
                      fontWeight: 600,
                    },
                  }}
                  helperText="Auto-set. Cannot be edited."
                />

                <TextField
                  label="Service Charge"
                  fullWidth
                  margin="dense"
                  name="serviceCharge"
                  value={formState.serviceCharge}
                  onChange={handleChange}
                  error={!!errors.serviceCharge}
                  helperText={errors.serviceCharge}
                  type="number"
                  inputProps={{ min: 0, step: "0.01" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Miscellaneous Price"
                  fullWidth
                  margin="dense"
                  name="miscellaneousPrice"
                  value={formState.miscellaneousPrice}
                  onChange={handleChange}
                  error={!!errors.miscellaneousPrice}
                  helperText={errors.miscellaneousPrice}
                  type="number"
                  inputProps={{ min: 0, step: "0.01" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Total Price"
                  fullWidth
                  margin="dense"
                  name="totalPrice"
                  disabled
                  value={formState.totalPrice}
                  onChange={handleChange}
                  error={!!errors.totalPrice}
                  helperText={
                    errors.totalPrice ||
                    "Auto-calculated. You can override if needed."
                  }
                  type="number"
                  inputProps={{ min: 0, step: "0.01" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f1f8e9",
                      fontWeight: 600,
                    },
                  }}
                />
              </>
            )}
          </Col>

          <Col lg={8}>
            <MapLocation
              booking={booking}
              setLatitude={setLatitude}
              setLongitude={setLongitude}
              latitude={latitude}
              longitude={longitude}
            />
          </Col>
        </Row>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          variant="contained"
          color="error"
          style={{ textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="success"
          style={{ textTransform: "none" }}
        >
          {booking ? "Update" : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
    <Dialog open={refundDialogOpen} onClose={handleRefundDialogClose}>
        <DialogTitle>Refund Confirmation</DialogTitle>
        <DialogContent>
        <DialogContentText>
  Are you sure you want to cancel this booking? Please confirm if a refund should be issued to the customer.
</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleRefundChoice(false)} color="inherit">
            No
          </Button>
          <Button
            onClick={() => handleRefundChoice(true)}
            variant="contained"
            color="success"
          >
            Yes, Refund
          </Button>
        </DialogActions>
      </Dialog>
    </>


  );
};

export default BookingAddEditForm;